// Core imports
import { Fragment, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import 'moment-duration-format';

// Component imports
import { Col, DatePicker, Form, Row } from 'antd';
import AgentsPicker from '../Forms/Fields/AgentsPicker';
import CustomRangePicker from '../Forms/Fields/CustomRangePicker';

// Misc imports
import { DATETIME_BACKEND_FORMAT, getPresetDateRanges } from '../../constants/main';
import { setObservatorySearchParams } from '../../store/global/globalActions';

const getDefaultStartDate = (timezone) => {
  return moment.tz(moment(), timezone).startOf('day');
};

const getDefaultEndDate = (timezone) => {
  return moment.tz(moment(), timezone).endOf('day');
};

export const useObservatorySearchParams = (timezone) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const { observatorySearchParams: savedParams } = useSelector((state) => state.global);

  const startDateParam = searchParams.get('startDate') || savedParams.startDate;
  const endDateParam = searchParams.get('endDate') || savedParams.endDate;
  const tenantsFilterParam = searchParams.get('tenantsFilter') || savedParams.tenantsFilter;

  // Return object that can be spread directly into urlParams
  const observatorySearchParams = {};

  if (startDateParam) {
    if (!timezone) {
      observatorySearchParams.startDate = startDateParam || '';
    } else {
      observatorySearchParams.startDate = `${startDateParam}${encodeURIComponent(moment().tz(timezone)?.format('Z'))}` || '';
    }
  }

  if (endDateParam) {
    if (!timezone) {
      observatorySearchParams.endDate = endDateParam || '';
    } else {
      observatorySearchParams.endDate = `${endDateParam}${encodeURIComponent(moment().tz(timezone)?.format('Z'))}` || '';
    }
  }

  if (tenantsFilterParam && JSON.parse(decodeURIComponent(tenantsFilterParam))?.length > 0) {
    observatorySearchParams.tenantsFilter = tenantsFilterParam;
  } else {
    observatorySearchParams.tenantsFilter = [];
  }

  return observatorySearchParams;
};

/* Get the observatory filters from the URL and convert them to initial form values
  * @param {string} timezone - The timezone to use for date parsing
  * @returns {object} - The observatory filters, which is:
  * {
  * startDate: moment object or undefined,
  * endDate: moment object or undefined,
  * tenantsFilter: array of tenant/deployment objects or empty array
  * }
*/
export const useObservatoryFilters = (timezone) => {
  const observatorySearchParams = useObservatorySearchParams(timezone);
  const startDateParam = observatorySearchParams.startDate;
  const endDateParam = observatorySearchParams.endDate;
  const tenantsFilterParam = observatorySearchParams.tenantsFilter;

  // Return object that can be spread directly into form initial values
  const observatoryFilters = {};

  if (startDateParam) {
    observatoryFilters.startDate = moment.tz(decodeURIComponent(startDateParam), DATETIME_BACKEND_FORMAT, timezone);
  } else {
    observatoryFilters.startDate = getDefaultStartDate(timezone);
  }

  if (endDateParam) {
    observatoryFilters.endDate = moment.tz(decodeURIComponent(endDateParam), DATETIME_BACKEND_FORMAT, timezone);
  } else {
    observatoryFilters.endDate = getDefaultEndDate(timezone);
  }

  if (tenantsFilterParam) {
    try {
      observatoryFilters.tenantsFilter = JSON.parse(decodeURIComponent(tenantsFilterParam));
    } catch (e) {
      observatoryFilters.tenantsFilter = [];
    }
  } else {
    observatoryFilters.tenantsFilter = [];
  }

  return observatoryFilters;
};

export const useInitialTenantFilters = () => {
  const filters = useObservatoryFilters();
  return filters.tenantsFilter.map(filter => {
    let checkedKeys = [];

    // If the whole tenant is selected (tenantId only - no deployments), add the tenantId to the checkedKeys
    if (filter.tenant && !filter.deployments && !filter.includeOtherDeployments) {
      checkedKeys.push(`${filter.tenant}`);
    }

    if (filter.includeOtherDeployments) {
      checkedKeys.push(`${filter.tenant}-includeOtherDeployments`);
    }

    if (filter.deployments?.length > 0) {
      filter.deployments.forEach(deployment => {
        checkedKeys.push(`${filter.tenant}-${deployment}`);
      });
    }

    return checkedKeys;
  }).flat() || [];
};

const useUpdateObservatorySearchParams = (form, timezone) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const convertTenantFiltersToSearchParams = () => {
    const checkedKeys = form.getFieldValue('tenantsFilter') || [];
    const filtersToReturn = [];

    // Map to store tenants and their associated deployments
    const tenantMap = {};

    // Loop through the checked keys to organize tenant and deployment data
    checkedKeys.forEach((key) => {
      const [tenantId, deploymentId] = key.split('-');

      // If there's no deployment ID, this key represents a tenant selection
      if (!deploymentId) {
        if (!tenantMap[tenantId]) {
          tenantMap[tenantId] = {
            tenant: tenantId,
            deployments: [],
            includeOtherDeployments: false
          };
        }
      } else if (deploymentId === 'includeOtherDeployments') {
        // If 'includeOtherDeployments' is part of the key, mark it in the tenant object
        if (!tenantMap[tenantId]) {
          tenantMap[tenantId] = {
            tenant: tenantId,
            deployments: [],
            includeOtherDeployments: true
          };
        } else {
          tenantMap[tenantId].includeOtherDeployments = true;
        }
      } else {
        // Otherwise, it's a deployment selection
        if (!tenantMap[tenantId]) {
          tenantMap[tenantId] = {
            tenant: tenantId,
            deployments: [deploymentId],
            includeOtherDeployments: false
          };
        } else {
          tenantMap[tenantId].deployments.push(deploymentId);
        }
      }
    });

    // Convert the tenantMap into an array for use as a query parameter
    Object.keys(tenantMap).forEach((tenantId) => {
      filtersToReturn.push(tenantMap[tenantId]);
    });

    return filtersToReturn;
  };

  const updateObservatorySearchParams = () => {
    const searchParams = new URLSearchParams(window.location.search);

    let startDate = searchParams.get('startDate') ? moment(decodeURIComponent(searchParams.get('startDate')), DATETIME_BACKEND_FORMAT) : getDefaultStartDate(timezone);
    let endDate = searchParams.get('endDate') ? moment(decodeURIComponent(searchParams.get('endDate')), DATETIME_BACKEND_FORMAT) : getDefaultEndDate(timezone);
    let tenantsFilter = [];

    const rangePickerArray = form.getFieldValue('rangePicker') || [];
    if (rangePickerArray.length === 2) {
      startDate = rangePickerArray[0];
      endDate = rangePickerArray[1];
    }
    tenantsFilter = convertTenantFiltersToSearchParams();

    if (startDate) {
      const plainDateString = startDate.format(DATETIME_BACKEND_FORMAT);
      searchParams.set('startDate', encodeURIComponent(`${plainDateString}`));
    } else {
      searchParams.delete('startDate');
    }

    if (endDate) {
      const plainDateString = endDate.format(DATETIME_BACKEND_FORMAT);
      searchParams.set('endDate', encodeURIComponent(`${plainDateString}`));
    } else {
      searchParams.delete('endDate');
    }

    searchParams.set('tenantsFilter', encodeURIComponent(JSON.stringify(tenantsFilter)));

    // Keep the new params in redux for future use
    dispatch(setObservatorySearchParams({
      startDate: searchParams.get('startDate'),
      endDate: searchParams.get('endDate'),
      tenantsFilter: searchParams.get('tenantsFilter')
    }));

    // Set the filters to the url
    navigate({
      pathname: location.pathname,
      search: searchParams.toString()
    }, { replace: true });
  };

  return { updateObservatorySearchParams };
};

const ObservatoryFiltersRow = ({
  form,
  timezone = 'UTC',
  rangePickerProps = {},
  agentsPickerProps = {},
  showDivider = false
}) => {
  const { t } = useTranslation();
  const { updateObservatorySearchParams } = useUpdateObservatorySearchParams(form, timezone);

  // On mount
  useEffect(() => {
    // Timeout is necessary to ensure other url params are set first
    setTimeout(() => {
      updateObservatorySearchParams();
    }, 0);
  }, []);

  return (
    (rangePickerProps.hidden && agentsPickerProps.hidden) ? null : (
      <Fragment>
        <Row gutter={[16, 16]} className="mb-4">
          {
            rangePickerProps.hidden ? null : (
              <Col flex="none">
                {
                  rangePickerProps.type === 'week' ? (
                    <Fragment>
                      <Form.Item
                        name="weekPicker"
                        initialValue={form.getFieldValue('endDate') ? moment(form.getFieldValue('endDate')) : moment().endOf('week')}
                        normalize={value => {
                          // We want to keep the end of the week as our true value
                          return moment(value).endOf('week');
                        }}
                        noStyle
                      >
                        <DatePicker
                          picker="week"
                          size={rangePickerProps.size || 'large'}
                          allowClear={rangePickerProps.allowClear || false}
                          style={rangePickerProps.style || { width: 351 }} // To match the width of the CustomRangePicker
                          disabledDate={rangePickerProps.disabledDate || ((current) => {
                            // Disable future weeks
                            return moment(current).startOf('week').isAfter(moment());
                          })}
                          format={rangePickerProps.format || ((value) => {
                            // Still display the week range as before
                            const weekStart = moment(value).startOf('week');
                            const weekEnd = moment(value).endOf('week');
                            return `${weekStart.format('DD MMM YYYY')} - ${weekEnd.format('DD MMM YYYY')}`;
                          })}
                          onChange={(date) => {
                            // Cloning is necessary to avoid mutating the original date object
                            const startDate = date ? date.clone().startOf('week') : null;
                            const endDate = date ? date.clone().endOf('week') : null;

                            form.setFieldsValue({ rangePicker: date ? [
                              startDate,
                              endDate
                            ] : null });

                            updateObservatorySearchParams();
                          }}
                          {...rangePickerProps}
                        />
                      </Form.Item>

                      <Form.Item
                        name="rangePicker"
                        hidden
                      >
                        <input type="hidden" />
                      </Form.Item>
                    </Fragment>
                  ): (
                    <Form.Item
                      name="rangePicker"
                      rules={[{
                        required: false,
                        message: t('Please select a date range.')
                      }]}
                      noStyle
                    >
                      <CustomRangePicker
                        size={rangePickerProps.size || 'large'}
                        disabled={rangePickerProps.disabled || false}
                        allowClear={rangePickerProps.allowClear || false}
                        disabledDate={rangePickerProps.disabledDate || ((current) => {
                          return current && current > getDefaultEndDate(timezone);
                        })}
                        ranges={rangePickerProps.ranges || getPresetDateRanges(timezone) || {}}
                        timezone={timezone}
                        onChange={(dates) => {
                          if (dates === null) {
                            form.setFieldsValue({ rangePicker: [
                              getDefaultStartDate(timezone),
                              getDefaultEndDate(timezone)
                            ] });
                          } else {
                            form.setFieldsValue({ rangePicker: [
                              dates[0],
                              dates[1]
                            ] });
                          }

                          updateObservatorySearchParams();
                        }}
                        {...rangePickerProps}
                      />
                    </Form.Item>
                  )
                }
              </Col>
            )
          }

          {
            agentsPickerProps.hidden ? null : (
              <Col flex="none">
                <Form.Item
                  name="tenantsFilter"
                  noStyle
                >
                  <AgentsPicker
                    agentsList={agentsPickerProps.agentsList || []}
                    onChange={updateObservatorySearchParams}
                    {...agentsPickerProps}
                  />
                </Form.Item>
              </Col>
            )}
        </Row>

        {showDivider && <div className="gradient-border mb-4" />}
      </Fragment>
    )
  );
};

ObservatoryFiltersRow.propTypes = {
  form: PropTypes.object.isRequired,
  timezone: PropTypes.string,
  rangePickerProps: PropTypes.object,
  agentsPickerProps: PropTypes.object,
  showDivider: PropTypes.bool
};

export default ObservatoryFiltersRow;
